import React from "react";

// Single-file React entry used by Codex/Canvas. App renders a centered
// sample size calculator. This file can be used as src/index.tsx or
// src/App.tsx depending on your bundler setup.

const Z_ALPHA = 1.96; // z-score for alpha = 0.05 (two-sided)
const Z_BETA = 0.84; // z-score for 80% power

const SampleSizeCalculator: React.FC = () => {
  const [baselineRate, setBaselineRate] = React.useState<number>(15); // %
  const [mde, setMde] = React.useState<number>(2); // percentage points
  const [variants, setVariants] = React.useState<number>(2); // # of arms including control

  const parseNumber = (value: number | string): number => {
    const n = typeof value === "number" ? value : parseFloat(value);
    return Number.isNaN(n) ? 0 : n;
  };

  const getResult = () => {
    const p1 = parseNumber(baselineRate) / 100; // baseline as 0–1
    const diff = parseNumber(mde) / 100; // MDE as 0–1
    const k = Math.max(1, Math.round(parseNumber(variants)));

    if (p1 <= 0 || p1 >= 1 || diff <= 0 || p1 + diff >= 1) {
      return {
        error:
          "Check your inputs. Rates must be between 0% and 100%, and baseline + lift < 100%.",
        nPerVariant: 0,
        totalN: 0,
      };
    }

    const p2 = p1 + diff;
    const pBar = (p1 + p2) / 2;

    // Standard sample size formula for two-proportion z-test with equal group sizes
    const term1 = Z_ALPHA * Math.sqrt(2 * pBar * (1 - pBar));
    const term2 = Z_BETA * Math.sqrt(p1 * (1 - p1) + p2 * (1 - p2));
    const nPerVariant = Math.ceil((term1 + term2) ** 2 / diff ** 2);
    const totalN = nPerVariant * k;

    return { error: null as string | null, nPerVariant, totalN };
  };

  const result = getResult();

  return (
    <div className="bg-white p-5 shadow-lg rounded-xl">
      <h2 className="text-xl font-bold mb-2">
        A/B Test Sample Size Calculator
      </h2>
      <p className="text-slate-600 text-sm mb-4">
        Assumes <strong>5% significance</strong> &amp;{" "}
        <strong>80% power</strong>.
      </p>

      <div className="flex flex-col gap-3 mb-5">
        <label>
          <span className="text-sm">Baseline conversion rate (%)</span>
          <input
            type="number"
            value={baselineRate}
            min={0}
            max={100}
            step={0.1}
            onChange={(e) => setBaselineRate(parseFloat(e.target.value))}
            className="w-full mt-1 p-2 border rounded-lg"
          />
        </label>

        <label>
          <span className="text-sm">Minimum detectable difference (pp)</span>
          <input
            type="number"
            value={mde}
            min={0}
            max={100}
            step={0.1}
            onChange={(e) => setMde(parseFloat(e.target.value))}
            className="w-full mt-1 p-2 border rounded-lg"
          />
        </label>

        <label>
          <span className="text-sm">
            Number of variations (including control)
          </span>
          <input
            type="number"
            min={2}
            step={1}
            value={variants}
            onChange={(e) => setVariants(parseInt(e.target.value, 10))}
            className="w-full mt-1 p-2 border rounded-lg"
          />
        </label>
      </div>

      <div className="bg-slate-900 text-slate-200 p-4 rounded-lg">
        {result.error ? (
          <p className="text-red-300 text-sm">{result.error}</p>
        ) : (
          <>
            <p className="text-sm">
              Required per variation:
              <strong className="ml-1">
                {result.nPerVariant.toLocaleString()}
              </strong>
            </p>
            <p className="text-sm mt-1">
              Total for {variants} variations:
              <strong className="ml-1">{result.totalN.toLocaleString()}</strong>
            </p>
          </>
        )}
      </div>
    </div>
  );
};

const App: React.FC = () => {
  return (
    <div className="app-frame p-6 bg-slate-100 min-h-screen flex items-center justify-center">
      <div className="w-full max-w-xl">
        <SampleSizeCalculator />
      </div>
    </div>
  );
};

export default App;
